import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { TDocumentDefinitions, Content } from 'pdfmake/interfaces';
import JsBarcode from 'jsbarcode';
import type { PdfCompanyInfo } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(pdfMake as any).vfs = (pdfFonts as any).vfs;

const MM_TO_PT = 2.83465;

export type TicketElementKey = 'companyName' | 'name' | 'reference' | 'price' | 'barcode';

export const DEFAULT_TICKET_ORDER: TicketElementKey[] = ['companyName', 'name', 'reference', 'price', 'barcode'];

export interface ArticleTicketData {
  name: string;
  reference: string;
  barcode: string | null;
  price: number;
}

export interface TicketSettings {
  widthMm: number;
  heightMm: number;
  showName: boolean;
  showPrice: boolean;
  showReference: boolean;
  showBarcode: boolean;
  showCompanyName: boolean;
  currencySymbol: string;
  elementOrder: TicketElementKey[];
}

export interface ArticleTicketBatch {
  article: ArticleTicketData;
  quantity: number;
}

function fmtPrice(n: number): string {
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function renderBarcodeSvg(value: string, heightPx: number): string | null {
  try {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    JsBarcode(svg, value, {
      format: 'CODE128',
      displayValue: true,
      fontSize: 16,
      font: 'Helvetica',
      textMargin: 2,
      width: 2,
      height: heightPx,
      margin: 0,
    });
    return new XMLSerializer().serializeToString(svg);
  } catch {
    return null;
  }
}

function isElementVisible(key: TicketElementKey, settings: TicketSettings): boolean {
  switch (key) {
    case 'companyName': return settings.showCompanyName;
    case 'name': return settings.showName;
    case 'reference': return settings.showReference;
    case 'price': return settings.showPrice;
    case 'barcode': return settings.showBarcode;
  }
}

function resolveOrder(settings: TicketSettings): TicketElementKey[] {
  const seen = new Set<string>();
  const ordered: TicketElementKey[] = [];
  settings.elementOrder?.forEach((k) => {
    if (DEFAULT_TICKET_ORDER.includes(k) && !seen.has(k)) {
      ordered.push(k);
      seen.add(k);
    }
  });
  DEFAULT_TICKET_ORDER.forEach((k) => {
    if (!seen.has(k)) ordered.push(k);
  });
  return ordered;
}

function buildTicketStack(
  company: PdfCompanyInfo,
  article: ArticleTicketData,
  settings: TicketSettings,
  contentWidthPt: number,
  contentHeightPt: number,
): Content {
  const stack: Content[] = [];
  const order = resolveOrder(settings);

  for (const key of order) {
    if (!isElementVisible(key, settings)) continue;

    switch (key) {
      case 'companyName':
        if (company.companyName) stack.push({ text: company.companyName, style: 'companyName' });
        break;
      case 'name':
        if (article.name) stack.push({ text: article.name, style: 'articleName' });
        break;
      case 'reference':
        if (article.reference) stack.push({ text: article.reference, style: 'articleReference' });
        break;
      case 'price':
        stack.push({
          text: `${fmtPrice(article.price)} ${settings.currencySymbol}`.trim(),
          style: 'articlePrice',
        });
        break;
      case 'barcode':
        if (article.barcode) {
          const barcodeHeightPt = Math.min(contentHeightPt * 0.45, 50);
          const svg = renderBarcodeSvg(article.barcode, 80);
          if (svg) {
            stack.push({
              svg,
              width: contentWidthPt,
              height: barcodeHeightPt,
              alignment: 'center',
            });
          } else {
            stack.push({ text: article.barcode, style: 'articleReference', alignment: 'center' });
          }
        }
        break;
    }
  }

  return { stack, alignment: 'center' };
}

export function generateArticleTicketPdf(
  company: PdfCompanyInfo,
  batches: ArticleTicketBatch[],
  settings: TicketSettings,
  action: 'print' | 'download' | 'open' = 'print',
): void {
  const width = settings.widthMm * MM_TO_PT;
  const height = settings.heightMm * MM_TO_PT;
  const margin = Math.min(width, height) * 0.05;
  const contentWidth = width - margin * 2;
  const contentHeight = height - margin * 2;

  const pages: Content[] = [];
  batches.forEach((batch, batchIdx) => {
    const qty = Math.max(1, Math.floor(batch.quantity || 1));
    for (let i = 0; i < qty; i++) {
      const isLast = batchIdx === batches.length - 1 && i === qty - 1;
      pages.push({
        stack: [buildTicketStack(company, batch.article, settings, contentWidth, contentHeight)],
        ...(isLast ? {} : { pageBreak: 'after' as const }),
      });
    }
  });

  if (pages.length === 0) return;

  const baseFontSize = Math.max(6, Math.min(12, Math.round(height * 0.08)));

  const docDef: TDocumentDefinitions = {
    pageSize: { width, height },
    pageMargins: [margin, margin, margin, margin],
    content: pages,
    defaultStyle: { font: 'Roboto', fontSize: baseFontSize },
    styles: {
      companyName: { fontSize: baseFontSize - 1, bold: true, alignment: 'center' },
      articleName: { fontSize: baseFontSize + 1, bold: true, alignment: 'center' },
      articleReference: { fontSize: baseFontSize - 1, alignment: 'center' },
      articlePrice: { fontSize: baseFontSize + 2, bold: true, alignment: 'center' },
    },
  };

  const pdf = pdfMake.createPdf(docDef);
  if (action === 'download') {
    pdf.download('article-tickets.pdf');
  } else if (action === 'open') {
    pdf.open();
  } else {
    pdf.print();
  }
}
